import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Menu, Input, Badge, Button } from 'antd';
import { NavLink, useRouteMatch, useLocation } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import propTypes from 'prop-types';
import { Scope } from "../services/scopeService";
import { Auth } from "../services/authService";
import "./menu.css";
import { fetchNewStudents } from "../redux/students/students/actionCreator";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchStudentUnseenEmails,
    fetchTeacherUnseenEmails,
    fetchUnseenComments,
    updateCommentSeenStatus,
    fetchUnseenMessages,
    updateMessageSeenStatus,
    fetchUnseenCVs,
    updateCVsSeenStatus,
    updatePermissionUpdateSeenStatus,
    fetchUnseenOfficialEmails,
    fetchUnseenPermissionUpdate,
    fetchUnseenTalentCVs,
    updateTalentCVsSeenStatus,
    fetchClassTeacherZoomUnseenEmails
} from "../redux/notifications/actionCreator";
import { fetchComment } from "../redux/admin/actionCreator";

const { SubMenu } = Menu;

const MenuItems = ({ darkMode, toggleCollapsed, topMenu }) => {
    const dispatch = useDispatch();
    const { path } = useRouteMatch();
    const location = useLocation();
    const userInfo = Auth.getUserInfo();
    const currentPath = location.pathname;

    const newStudents = useSelector(state => state.studentsStudents.newStudents);
    const teacherUnseenEmails = useSelector(state => state.notifications.teacherUnseenEmails);
    const studentUnseenEmails = useSelector(state => state.notifications.studentUnseenEmails);
    const classTeacherZoomUnseenEmails = useSelector(state => state.notifications.classTeacherZoomUnseenEmails);
    const unseenComments = useSelector(state => state.notifications.unseenComments);
    const unseenMessages = useSelector(state => state.notifications.unseenMessages);
    const unseenCVs = useSelector(state => state.notifications.unseenCVs);
    const unseenTalentCVs = useSelector(state => state.notifications.unseenTalentCVs);
    const unseenOfficialEmails = useSelector(state => state.notifications.unseenOfficialEmails);
    const unseenPermissionUpdate = useSelector(state => state.notifications.unseenPermissionUpdate);

    const comment = useSelector(state => state.allCareers.comment);

    // Search & Screen state
    const [searchQuery, setSearchQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [screenWidth, setScreenWidth] = useState(() => window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            if (!isFocused && !isMobileSearchOpen) {
                setScreenWidth(window.innerWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isFocused, isMobileSearchOpen]);

    // Open keys state for dynamic submenu expansion
    // AFTER
    const [openKeys, setOpenKeys] = useState([]);

    // 1. Compute active selected menu key dynamically from URL path
    const selectedKey = useMemo(() => {
        const parts = currentPath.split('/').filter(Boolean);
        if (parts.length === 0) return 'dashboard';

        const lastSegment = parts[parts.length - 1];

        if (currentPath.includes('/academic/reports/')) {
            return `${lastSegment}-report`;
        }
        if (currentPath.includes('/academic/result/')) {
            return lastSegment === 'half-yearly' ? 'half-yearly' : lastSegment;
        }

        return lastSegment;
    }, [currentPath]);

    // 2. Automatically expand parent submenus on route change
    // AFTER
    useEffect(() => {
        // Prevent auto-opening submenus on mobile views to stop floating popups on load
        if (screenWidth <= 991) {
            setOpenKeys([]);
            return;
        }

        const keysToOpen = [];

        if (currentPath.includes('/academic')) keysToOpen.push('academic-management');
        if (currentPath.includes('/academic/setup')) keysToOpen.push('setup-management');
        if (currentPath.includes('/academic/routines') || currentPath.includes('/academic/substitute') || currentPath.includes('/academic/pre-routine')) {
            keysToOpen.push('routine-management');
        }
        if (currentPath.includes('/academic/result')) keysToOpen.push('result-management');
        if (currentPath.includes('/academic/reports')) keysToOpen.push('reports-management');
        if (currentPath.includes('/user-management')) keysToOpen.push('user-management', 'permissions');
        if (currentPath.includes('/user-attendance')) keysToOpen.push('user-attendance');
        if (currentPath.includes('/admin/')) keysToOpen.push('admin-management');
        if (currentPath.includes('/domain-emails')) keysToOpen.push('domain-email', 'domain-email-all', 'student-domain-email-all');
        if (currentPath.includes('/IT/')) keysToOpen.push('it-component');
        if (currentPath.includes('/employee-salary')) keysToOpen.push('user-salary');
        if (currentPath.includes('/clubs/')) keysToOpen.push('club');

        setOpenKeys(prev => Array.from(new Set([...prev, ...keysToOpen])));
    }, [currentPath, screenWidth]);

    const onOpenChange = (keys) => {
        setOpenKeys(keys);
    };

    useEffect(() => {
        if (!userInfo?._id) return;

        const fetchData = async () => {
            try {
                if (Scope.checkScopes(['new_students_index'])) {
                    await dispatch(fetchNewStudents());
                }
                if (Scope.checkScopes(['domain_emails_teachers_index'])) {
                    await dispatch(fetchTeacherUnseenEmails());
                }
                if (Scope.checkScopes(['domain_emails_students_index'])) {
                    await dispatch(fetchStudentUnseenEmails());
                }
                if (Scope.checkScopes(['domain_emails_class_teacher_zoom_index'])) {
                    await dispatch(fetchClassTeacherZoomUnseenEmails());
                }
                if (Scope.checkScopes(['admin_comment_index'])) {
                    await dispatch(fetchUnseenComments());
                    await dispatch(fetchComment({ page: 1, perPage: 35 }));
                }
                if (Scope.checkScopes(['admin_message_index'])) {
                    await dispatch(fetchUnseenMessages());
                }
                if (Scope.checkScopes(['admin_career_index'])) {
                    await dispatch(fetchUnseenCVs());
                }
                if (Scope.checkScopes(['admin_talent_index'])) {
                    await dispatch(fetchUnseenTalentCVs());
                }
                if (Scope.checkScopes(['all_official_emails_index'])) {
                    await dispatch(fetchUnseenOfficialEmails());
                }
                if (Scope.checkScopes(['um_roles_permissions_history'])) {
                    await dispatch(fetchUnseenPermissionUpdate());
                }
            } catch (error) {
                console.error("Error fetching menu item data:", error);
            }
        };

        fetchData();
    }, [dispatch, userInfo?._id]);

    const markCommentsSeen = async () => {
        await dispatch(updateCommentSeenStatus());
    };
    const markMessagesSeen = async () => {
        await dispatch(updateMessageSeenStatus());
    };
    const markCVsSeen = async () => {
        await dispatch(updateCVsSeenStatus());
    };
    const markTalentCVsSeen = async () => {
        await dispatch(updateTalentCVsSeenStatus());
    };
    const markPermissionUpdateSeen = async () => {
        await dispatch(updatePermissionUpdateSeenStatus());
    };

    const unassignedStudentCount = (() => {
        const data = newStudents?.data || [];
        const flatStudents = Array.isArray(data[0]) ? data.flat() : data;
        return flatStudents.filter(student => !student.section?.name).length;
    })();

    const showAdminOfficeMenu =
        Scope.checkScopes(['admin_career_index']) ||
        Scope.checkScopes(['admin_talent_index']) ||
        Scope.checkScopes(['admin_message_index']) ||
        (Scope.checkScopes(['admin_comment_index']) && !!comment?.data?.length);

    // Navigation Tree for Search Indexing
    const allSearchableNavTree = useMemo(() => {
        const tree = [];

        if (Scope.checkScopes(['dashboard_index'])) {
            tree.push({ title: 'Dashboard', path: `${path}/dashboard` });
            tree.push({ title: 'Profile', path: `${path}/user-management/profile/${userInfo?._id}` });
        } else {
            tree.push({ title: 'Profile', path: `${path}/user-management/profile/${userInfo?._id}` });
        }

        if (Scope.checkScopesParent(['um_roles_permissions_'])) {
            const children = [];
            if (Scope.checkScopes(['um_roles_permissions_index'])) {
                children.push({ title: 'Permissions', path: `${path}/user-management/roles-permissions` });
            }
            if (Scope.checkScopes(['um_roles_permissions_history'])) {
                children.push({
                    title: 'Permission Change Logs',
                    path: `${path}/user-management/permissions`,
                    onClick: markPermissionUpdateSeen,
                    badgeCount: unseenPermissionUpdate?.data
                });
            }
            tree.push({ title: 'Permission Management', children });
        }

        if (Scope.checkScopesParent(['um_'])) {
            const children = [];
            if (Scope.checkScopes(['um_position_index'])) children.push({ title: 'Positions', path: `${path}/user-management/position` });
            if (Scope.checkScopes(['um_departments_index'])) children.push({ title: 'Departments', path: `${path}/user-management/departments` });
            if (Scope.checkScopes(['um_roles_index'])) children.push({ title: 'Roles', path: `${path}/user-management/roles` });
            if (Scope.checkScopes(['um_users_index'])) children.push({ title: 'Users', path: `${path}/user-management/users` });
            if (Scope.checkScopes(['students_portal_result_access'])) children.push({ title: 'Access Control', path: `${path}/user-management/student-access` });
            if (Scope.checkScopes(['result_publication_index'])) children.push({ title: 'Result Publications', path: `${path}/result-publication` });
            if (Scope.checkScopes(['um_logs_index'])) children.push({ title: 'User Logs', path: `${path}/user-management/logs` });
            if (Scope.checkScopes(['um_logs_index'])) children.push({ title: 'Student Logs', path: `${path}/user-management/student-logs` });
            if (Scope.checkScopes(['temp_controller_status'])) children.push({ title: 'Temp Controller Status', path: `${path}/user-management/temp-controller` });
            tree.push({ title: 'Administration', children });
        }

        if (Scope.checkScopes(['other_users_for_index'])) {
            tree.push({ title: "Users' List", path: `${path}/user-attendance/usersForOthers` });
        }

        if (Scope.checkScopesParent(['ut_'])) {
            const children = [];
            if (Scope.checkScopes(['ut_month_declarations_index'])) children.push({ title: 'Month Declarations', path: `${path}/user-attendance/monthDeclarations` });
            if (Scope.checkScopes(['ut_daySchedule_index'])) children.push({ title: 'Attendance Categories', path: `${path}/user-attendance/attendanceCategories` });
            if (Scope.checkScopes(['ut_specialDay_index'])) children.push({ title: 'Special Days', path: `${path}/user-attendance/special-days` });
            if (Scope.checkScopes(['ut_rosters_index'])) children.push({ title: 'Rostering', path: `${path}/user-attendance/rosterAttendances` });
            if (Scope.checkScopes(['ut_userAttendance_index'])) children.push({ title: 'User Attendances', path: `${path}/user-attendance/userAttendance` });
            if (Scope.checkScopes(['ut_user_monthly_attendances_index'])) children.push({ title: 'Individual Report', path: `${path}/user-attendance/userMonthlyAttendances` });
            if (Scope.checkScopes(['ut_monthly_attendances_index'])) children.push({ title: 'Monthly Summaries', path: `${path}/user-attendance/monthlyAttendances` });
            if (Scope.checkScopes(['ut_daySchedule_index'])) children.push({ title: 'Day Schedules', path: `${path}/user-attendance/daySchedule` });
            if (Scope.checkScopes(['shift_employee'])) children.push({ title: 'Shift Employee Attendance', path: `${path}/user-attendance/shiftEmployees` });
            if (Scope.checkScopes(['refetch_attendance_index'])) children.push({ title: 'Refetch Attendance', path: `${path}/user-attendance/refetch` });

            const leaveChildren = [];
            if (Scope.checkScopes(['ut_yearly_reserved_leave_index'])) leaveChildren.push({ title: 'Yearly Reserved Leaves', path: `${path}/user-attendance/yearlyReservedLeaves` });
            if (Scope.checkScopes(['ut_leave_index'])) leaveChildren.push({ title: 'General Leaves', path: `${path}/user-attendance/leave` });
            if (Scope.checkScopes(['ut_early_leave_index'])) leaveChildren.push({ title: 'Early Leaves', path: `${path}/user-attendance/earlyLeave` });
            if (Scope.checkScopes(['ut_sandwich_index'])) leaveChildren.push({ title: 'Sandwich Leaves', path: `${path}/user-attendance/sandwiches` });
            if (leaveChildren.length) children.push({ title: 'Leave Management', children: leaveChildren });

            tree.push({ title: 'User Attendance', children });
        }

        if (showAdminOfficeMenu) {
            const children = [];
            if (Scope.checkScopes(['admin_career_index'])) children.push({ title: 'Vacancy Post', path: `${path}/admin/careerPost` });
            if (Scope.checkScopes(['admin_career_index'])) children.push({ title: 'Direct Applications', path: `${path}/admin/career`, onClick: markCVsSeen, badgeCount: unseenCVs?.data });
            if (Scope.checkScopes(['admin_talent_index'])) children.push({ title: 'Prospective Talent', path: `${path}/admin/talentPools`, onClick: markTalentCVsSeen, badgeCount: unseenTalentCVs?.data });
            if (Scope.checkScopes(['admin_message_index'])) children.push({ title: 'Website Messages', path: `${path}/admin/message`, onClick: markMessagesSeen, badgeCount: unseenMessages?.data });
            if (Scope.checkScopes(['admin_comment_index']) && comment?.data?.length) children.push({ title: 'Parents Feedback', path: `${path}/admin/comment`, onClick: markCommentsSeen, badgeCount: unseenComments?.data });
            tree.push({ title: 'Admin Office', children });
        }

        if (Scope.checkScopesParent(['kpi_'])) {
            const children = [];
            if (Scope.checkScopes(['kpi_criteria_index'])) children.push({ title: 'KPI Criteria', path: `${path}/kpi/criteria` });
            if (Scope.checkScopes(['kpi_index'])) children.push({ title: 'KPI Users', path: `${path}/kpi/kpi-users` });
            tree.push({ title: 'KPI System', children });
        }

        if (Scope.checkScopesParent(['schedules_'])) {
            const children = [];
            if (Scope.checkScopes(['schedules_index'])) children.push({ title: 'Schedules', path: `${path}/schedules` });
            tree.push({ title: 'Schedule Management', children });
        }

        if (Scope.checkScopesParent(['website_'])) {
            const children = [];
            if (Scope.checkScopes(['blogs_index'])) children.push({ title: 'News & Events', path: `${path}/blogs/blogs` });
            if (Scope.checkScopes(['slides_index'])) children.push({ title: 'Slides', path: `${path}/slides/slides` });
            if (Scope.checkScopes(['galleries_index'])) children.push({ title: 'Gallery Images', path: `${path}/galleries/galleries` });
            if (Scope.checkScopes(['admission_slide_index'])) children.push({ title: 'Admission Slide Images', path: `${path}/admissionSlides/admissionSlides` });
            if (Scope.checkScopes(['result_slide_index'])) children.push({ title: 'Result Slide Images', path: `${path}/resultSlides/resultSlides` });
            if (Scope.checkScopes(['result_index'])) children.push({ title: 'O & A Level Result', path: `${path}/results/results` });
            tree.push({ title: 'Website Management', children });
        }

        if (Scope.checkScopesParent(['mailsBox_'])) {
            const children = [];
            if (Scope.checkScopes(['mailsBox__index'])) children.push({ title: 'Compose Emails', path: `${path}/emails/emails` });
            if (Scope.checkScopes(['be_forward_index'])) children.push({ title: 'Add/Forward', path: `${path}/emails/forward-to` });
            tree.push({ title: 'Email Forwarding', children });
        }

        if (Scope.checkScopesParent(['admitted_'])) {
            const children = [];
            if (Scope.checkScopes(['admitted_students_index'])) children.push({ title: 'All Students', path: `${path}/students/students` });
            if (Scope.checkScopes(['academic_Students_index'])) children.push({ title: 'Academic Students', path: `${path}/students/all-students` });
            if (Scope.checkScopes(['student_marks_entry_students_index'])) children.push({ title: 'Marks Entry Form', path: `${path}/students/marks-students` });
            tree.push({ title: 'Manage Students', children });
        }

        if (Scope.checkScopesParent([
            'academic_setup_periods_index', 'academic_setup_sessions_index', 'academic_setup_classes_index',
            'academic_setup_sections_index', 'academic_setup_subjects_index', 'academic_setup_payment_types_index',
            'routines_index', 'routines_substitute_index', 'routine_pre_routine_index', 'routines_pre_routine_index',
            'academic_coordination_index', 'academic_notices_index', 'academic_notes_index', 'academic_attendance_index',
            'subject_chosen_students_index', 'academic_subjective_attendance_index', 'academic_predicted_grade_index',
            'academic_predicted_grade_report', 'academic_payments_index', 'academic_transactions_index',
            'academic_my_students_index', 'academic_my_students_routine', 'student_id_card_index',
            'academic_result_quarter_index', 'academic_result_half_yearly_index', 'academic_result_final_index',
            'academic_result_mock_index', 'academic_reports_quarter_index', 'academic_reports_half_yearly_index',
            'academic_reports_final_index', 'academic_reports_mock_index', 'academic_reports_students_performance_index'
        ])) {
            const children = [];

            if (Scope.checkScopesParent(['academic_setup_periods_index', 'academic_setup_sessions_index', 'academic_setup_classes_index', 'academic_setup_sections_index', 'academic_setup_subjects_index', 'academic_setup_payment_types_index'])) {
                const setupKids = [];
                if (Scope.checkScopes(['academic_setup_periods_index'])) setupKids.push({ title: 'Periods', path: `${path}/academic/setup/periods` });
                if (Scope.checkScopes(['academic_setup_sessions_index'])) setupKids.push({ title: 'Sessions', path: `${path}/academic/setup/sessions` });
                if (Scope.checkScopes(['academic_setup_classes_index'])) setupKids.push({ title: 'Classes', path: `${path}/academic/setup/classes` });
                if (Scope.checkScopes(['academic_setup_sections_index'])) setupKids.push({ title: 'Sections', path: `${path}/academic/setup/sections` });
                if (Scope.checkScopes(['academic_setup_subjects_index'])) setupKids.push({ title: 'Subjects', path: `${path}/academic/setup/subjects` });
                if (Scope.checkScopes(['academic_setup_payment_types_index'])) setupKids.push({ title: 'Payment Types', path: `${path}/academic/setup/payment-types` });
                children.push({ title: 'Setup', children: setupKids });
            }

            if (Scope.checkScopesParent(['routines_index', 'routines_substitute_index', 'routine_pre_routine_index', 'routines_pre_routine_index'])) {
                const routineKids = [];
                if (Scope.checkScopes(['routines_index'])) routineKids.push({ title: 'Routines', path: `${path}/academic/routines` });
                if (Scope.checkScopes(['routines_substitute_index'])) routineKids.push({ title: 'Substitutes', path: `${path}/academic/substitute-routines` });
                if (Scope.checkScopes(['routine_pre_routine_index', 'routines_pre_routine_index'])) routineKids.push({ title: 'Pre Routines', path: `${path}/academic/pre-routine` });
                children.push({ title: 'Manage Routine', children: routineKids });
            }

            if (Scope.checkScopes(['academic_coordination_index'])) children.push({ title: 'Coordination', path: `${path}/academic/coordination` });
            if (Scope.checkScopes(['academic_notices_index'])) children.push({ title: 'Class Teacher Zone', path: `${path}/academic/notice` });
            if (Scope.checkScopes(['academic_notes_index'])) children.push({ title: 'Subject Notes & Classwork', path: `${path}/academic/note` });
            if (Scope.checkScopes(['academic_attendance_index'])) children.push({ title: 'Class Attendance', path: `${path}/academic/attendance` });
            if (Scope.checkScopes(['subject_chosen_students_index'])) children.push({ title: 'Subjective Students', path: `${path}/students/subject-chosen-students` });
            if (Scope.checkScopes(['academic_subjective_attendance_index'])) children.push({ title: 'Subjective Attendance', path: `${path}/academic/subjective-attendance` });
            if (Scope.checkScopes(['academic_predicted_grade_index'])) children.push({ title: 'Predicted Grades', path: `${path}/academic/predicted-grade` });
            if (Scope.checkScopes(['academic_predicted_grade_report'])) children.push({ title: 'Predicted Grade Reports', path: `${path}/academic/predicted-grade-report` });
            if (Scope.checkScopes(['academic_transcript_report'])) children.push({ title: 'Academic Transcript', path: `${path}/academic/academic-transcript-report` });
            if (Scope.checkScopes(['academic_payments_index'])) children.push({ title: 'Payments', path: `${path}/academic/payments` });
            if (Scope.checkScopes(['academic_transactions_index'])) children.push({ title: 'Transactions', path: `${path}/academic/transactions` });
            if (Scope.checkScopes(['academic_my_students_index'])) children.push({ title: 'My Students', path: `${path}/students/my-students` });
            if (Scope.checkScopes(['academic_my_students_routine'])) children.push({ title: 'My Class Routine', path: `${path}/students/my-students-routine` });
            if (Scope.checkScopes(['student_id_card_index'])) children.push({ title: 'ID Cards', path: `${path}/students/id-card` });

            if (Scope.checkScopesParent(['academic_result_quarter_index', 'academic_result_half_yearly_index', 'academic_result_final_index', 'academic_result_mock_index'])) {
                const marksKids = [];
                if (Scope.checkScopes(['academic_result_quarter_index'])) marksKids.push({ title: 'Quarter', path: `${path}/academic/result/quarter` });
                if (Scope.checkScopes(['academic_result_half_yearly_index'])) marksKids.push({ title: 'Half Yearly', path: `${path}/academic/result/half-yearly` });
                if (Scope.checkScopes(['academic_result_final_index'])) marksKids.push({ title: 'Final', path: `${path}/academic/result/final` });
                if (Scope.checkScopes(['academic_result_mock_index'])) marksKids.push({ title: 'Mock', path: `${path}/academic/result/mock` });
                children.push({ title: 'Marks', children: marksKids });
            }

            if (Scope.checkScopesParent(['academic_reports_quarter_index', 'academic_reports_half_yearly_index', 'academic_reports_final_index', 'academic_reports_mock_index', 'academic_reports_students_performance_index'])) {
                const reportKids = [];
                if (Scope.checkScopes(['academic_reports_quarter_index'])) reportKids.push({ title: 'Quarter', path: `${path}/academic/reports/quarter` });
                if (Scope.checkScopes(['academic_reports_half_yearly_index'])) reportKids.push({ title: 'Half Yearly', path: `${path}/academic/reports/half-yearly` });
                if (Scope.checkScopes(['academic_reports_final_index'])) reportKids.push({ title: 'Final', path: `${path}/academic/reports/final` });
                if (Scope.checkScopes(['academic_reports_mock_index'])) reportKids.push({ title: 'Mock', path: `${path}/academic/reports/mock` });
                if (Scope.checkScopes(['academic_reports_students_performance_index'])) reportKids.push({ title: 'Performance', path: `${path}/academic/reports/performance` });
                children.push({ title: 'Reports', children: reportKids });
            }

            tree.push({ title: 'Academic', children });
        }

        if (Scope.checkScopes(['academic_section_students_index'])) tree.push({ title: 'Students', path: `${path}/students/section-students` });
        if (Scope.checkScopes(['academic_subject_students_index'])) tree.push({ title: 'Subjective Mock Reports', path: `${path}/students/subject-students` });

        if (Scope.checkScopes(['new_students_index'])) {
            tree.push({
                title: 'New Students',
                path: `${path}/students/new-students`,
                badgeCount: unassignedStudentCount
            });
        }

        if (Scope.checkScopesParent(['student_fingerprint_']) || Scope.checkScopesParent(['student_holiday_'])) {
            const children = [];
            if (Scope.checkScopes(['student_fingerprint_sms_index'])) children.push({ title: 'Check In & Out', path: `${path}/message` });
            if (Scope.checkScopes(['student_holiday_sms_index'])) children.push({ title: 'HoliDay SMS Status', path: `${path}/holiDaySMS` });
            tree.push({ title: "Students' Attendance", children });
        }

        if (Scope.checkScopesParent(['class_marks_'])) {
            const children = [];
            if (Scope.checkScopes(['class_marks_input_permission_view'])) {
                children.push({ title: 'Permission View', path: `${path}/marksInputPermission` });
                children.push({ title: 'Allow Users Marks Input', path: `${path}/allowUserMarksInput` });
            }
            tree.push({ title: 'Marks Input Permission', children });
        }

        if (Scope.checkScopesParent(['attendance_permission_'])) {
            const children = [];
            if (Scope.checkScopes(['attendance_input_permission_view'])) {
                children.push({ title: 'Attend Permission View', path: `${path}/attendanceInputPermission` });
                children.push({ title: 'Allow Users Attend Input', path: `${path}/allowUserAttendanceInput` });
            }
            tree.push({ title: 'Attend Input Permission', children });
        }

        if (Scope.checkScopesParent(['it_'])) {
            const children = [];
            if (Scope.checkScopes(['it_class_rooms_index'])) children.push({ title: 'Locations', path: `${path}/academic/setup/classrooms` });
            if (Scope.checkScopes(['it_product_category_index'])) children.push({ title: 'Product Categories', path: `${path}/IT/productCategories` });
            if (Scope.checkScopes(['it_component_index'])) children.push({ title: 'Component List', path: `${path}/IT/itComponents` });
            if (Scope.checkScopes(['it_component_index'])) children.push({ title: 'IP Addresses', path: `${path}/IT/ipAddresses` });
            tree.push({ title: 'IT Components', children });
        }

        if (Scope.checkScopesParent(['salary_'])) {
            const children = [];
            if (Scope.checkScopes(['salary_declaration_index'])) children.push({ title: 'Salary Declarations', path: `${path}/employee-salary/salaryDeclaration` });
            if (Scope.checkScopes(['salary_month_declaration_index'])) children.push({ title: 'Salary Month Declarations', path: `${path}/employee-salary/SalaryMonthDeclaration` });
            if (Scope.checkScopes(['salary_increment_month_declaration_index'])) children.push({ title: 'Salary Inc. Month Declarations', path: `${path}/employee-salary/SalaryIncrementMonthDeclaration` });
            if (Scope.checkScopes(['monthly_salary_index'])) children.push({ title: 'Monthly Salary Generation', path: `${path}/employee-salary/MonthlySalary` });
            if (Scope.checkScopes(['provident_fund_employees_index'])) children.push({ title: 'PF Employees', path: `${path}/employee-salary/providentFundEmployees` });
            tree.push({ title: 'Employee Salary', children });
        }

        if (Scope.checkScopesParent(['domain_'])) {
            const children = [];
            if (Scope.checkScopes(['domain_emails_teachers_index'])) children.push({ title: 'My Official Emails', path: `${path}/domain-emails/teachers`, badgeCount: teacherUnseenEmails?.data });
            if (Scope.checkScopes(['domain_emails_students_index'])) children.push({ title: "Students' Emails", path: `${path}/domain-emails/students`, badgeCount: studentUnseenEmails?.data });
            if (Scope.checkScopes(['domain_emails_class_teacher_zoom_index'])) children.push({ title: 'Class-Teacher Zoom', path: `${path}/domain-emails/class-teacher-zoom-email`, badgeCount: classTeacherZoomUnseenEmails?.data });
            if (Scope.checkScopes(['email_group_index'])) children.push({ title: 'Email Group', path: `${path}/emailGroup` });
            tree.push({ title: 'Emails', children });
        }

        if (Scope.checkScopesParent(['all_d_'])) {
            const children = [];
            if (Scope.checkScopes(['all_d_teachers_info'])) children.push({ title: "All Teacher's Zoom Info", path: `${path}/domain-emails/zoom-teachers` });
            tree.push({ title: 'All Zoom Info', children });
        }

        if (Scope.checkScopesParent(['all_d_students_'])) {
            const children = [];
            if (Scope.checkScopes(['all_d_students_info'])) children.push({ title: "All Students' Zoom Info", path: `${path}/domain-emails/zoom-students` });
            if (Scope.checkScopes(['all_d_students_index'])) children.push({ title: "All Students' Emails", path: `${path}/domain-emails/all-zoom-students` });
            tree.push({ title: "All Students' Zoom", children });
        }

        if (Scope.checkScopes(['all_official_emails_index'])) {
            tree.push({ title: 'All Official Emails', path: `${path}/domain-emails/all-official`, badgeCount: unseenOfficialEmails?.data });
        }

        if (Scope.checkScopesParent(['appointed_'])) {
            const children = [];
            if (Scope.checkScopes(['appointed_teachers_index'])) children.push({ title: 'Appointed Teachers', path: `${path}/appointment/appointed-teachers` });
            if (Scope.checkScopes(['appointments_index'])) children.push({ title: 'Appointments', path: `${path}/appointment/get-appointments` });
            tree.push({ title: 'Appointments', children });
        }

        if (Scope.checkScopesParent(['mcw_schedule_'])) {
            const children = [];
            if (Scope.checkScopes(['mcw_schedule_index'])) children.push({ title: 'Manage Schedules', path: `${path}/mcwSchedule` });
            tree.push({ title: 'MCW Schedule', children });
        }

        if (Scope.checkScopesParent(['exam_calendar_'])) {
            const children = [];
            if (Scope.checkScopes(['exam_calendar_index'])) children.push({ title: 'Exam Calendar', path: `${path}/examCalendar` });
            tree.push({ title: 'Exam Calendar', children });
        }

        if (Scope.checkScopesParent(['clubs_'])) {
            const children = [];
            if (Scope.checkScopes(['main_clubs_index'])) children.push({ title: 'Main Clubs', path: `${path}/clubs/mainClub` });
            if (Scope.checkScopes(['sub_clubs_index'])) children.push({ title: 'Sub Clubs', path: `${path}/clubs/subClub` });
            if (Scope.checkScopes(['student_club_memberships_index'])) children.push({ title: 'Student Membership Club', path: `${path}/clubs/studentMembershipClub` });
            tree.push({ title: 'Clubs', children });
        }

        if (Scope.checkScopesParent(['ct_schedule_'])) {
            const children = [];
            if (Scope.checkScopes(['ct_schedule_index'])) children.push({ title: 'Manage Schedules', path: `${path}/ctSchedule` });
            if (Scope.checkScopes(['ct_schedule_view_index'])) children.push({ title: 'View Schedules', path: `${path}/ctScheduleView` });
            tree.push({ title: 'CT Schedule', children });
        }

        if (Scope.checkScopesParent(['invoice_'])) {
            const children = [];
            if (Scope.checkScopes(['invoice_index'])) children.push({ title: 'General Invoice', path: `${path}/invoice` });
            if (Scope.checkScopes(['email_server_invoice_index'])) children.push({ title: 'Email Server Invoice', path: `${path}/email-server-invoice` });
            tree.push({ title: 'Billing Section', children });
        }

        return tree;
    }, [path, userInfo?._id, showAdminOfficeMenu, unseenPermissionUpdate?.data, unseenCVs?.data, unseenTalentCVs?.data, unseenMessages?.data, unseenComments?.data, comment?.data?.length, unassignedStudentCount, teacherUnseenEmails?.data, studentUnseenEmails?.data, classTeacherZoomUnseenEmails?.data, unseenOfficialEmails?.data]);

    // Search Matching Logic
    const filteredSearchResults = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return [];

        const results = [];

        const filterNode = (node, parentBreadcrumb = []) => {
            const currentBreadcrumb = [...parentBreadcrumb, node.title];
            const titleMatches = node.title.toLowerCase().includes(query);

            if (node.path) {
                if (titleMatches || parentBreadcrumb.some(p => p.toLowerCase().includes(query))) {
                    results.push({
                        title: node.title,
                        path: node.path,
                        breadcrumb: parentBreadcrumb.join(' > '),
                        onClick: node.onClick,
                        badgeCount: node.badgeCount
                    });
                }
            }

            if (node.children && node.children.length > 0) {
                node.children.forEach(child => filterNode(child, currentBreadcrumb));
            }
        };

        allSearchableNavTree.forEach(node => filterNode(node));

        return results;
    }, [searchQuery, allSearchableNavTree]);

    const stopPropagation = (e) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
    };

    const handleInputFocus = () => {
        setIsFocused(true);
        if (screenWidth <= 991) {
            setIsMobileSearchOpen(true);
        }
    };

    const handleCloseMobileSearch = () => {
        setIsMobileSearchOpen(false);
        setSearchQuery('');
        setIsFocused(false);
    };

    // React Portal Overlay specifically for Mobile Search
    const renderMobilePortal = () => {
        if (screenWidth > 991 || !isMobileSearchOpen) return null;

        return ReactDOM.createPortal(
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: '#ffffff',
                    zIndex: 999999,
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box'
                }}
            >
                {/* Search Bar & Close Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <Input
                        autoFocus
                        placeholder="Type to search menu items..."
                        prefix={<FeatherIcon icon="search" size={18} style={{ color: '#1677ff' }} />}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        allowClear
                        style={{ flex: 1, height: '44px', fontSize: '15px' }}
                    />
                    <Button
                        type="primary"
                        danger
                        onClick={handleCloseMobileSearch}
                        style={{ height: '44px', fontWeight: 'bold' }}
                    >
                        Close
                    </Button>
                </div>

                {/* Search Results Content Container */}
                <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    {searchQuery.trim() !== '' ? (
                        filteredSearchResults.length > 0 ? (
                            filteredSearchResults.map((item, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        padding: '12px 14px',
                                        borderBottom: '1px solid #f0f0f0',
                                        backgroundColor: '#fafafa',
                                        borderRadius: '6px',
                                        marginBottom: '8px'
                                    }}
                                >
                                    <NavLink
                                        to={item.path}
                                        onClick={async (e) => {
                                            if (item.onClick) await item.onClick();
                                            handleCloseMobileSearch();
                                            toggleCollapsed();
                                        }}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            color: '#262626',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            {item.breadcrumb && (
                                                <span style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                    {item.breadcrumb}
                                                </span>
                                            )}
                                            <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                                {item.title}
                                            </span>
                                        </div>
                                        {item.badgeCount > 0 && (
                                            <Badge
                                                count={item.badgeCount}
                                                overflowCount={99}
                                                style={{ backgroundColor: 'red' }}
                                            />
                                        )}
                                    </NavLink>
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', color: '#8c8c8c', marginTop: '30px' }}>
                                No matching menu items found.
                            </div>
                        )
                    ) : (
                        <div style={{ textAlign: 'center', color: '#bfbfbf', marginTop: '40px' }}>
                            Type to search menu navigation...
                        </div>
                    )}
                </div>
            </div>,
            document.body
        );
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {/* Search Box Container (Desktop Inline View) */}
            <div
                className="menu-search-container"
                onClick={stopPropagation}
                onTouchStart={stopPropagation}
                onMouseDown={stopPropagation}
            >
                <Input
                    placeholder="Search menu items..."
                    prefix={<FeatherIcon icon="search" size={16} style={{ color: '#8c8c8c' }} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={() => setIsFocused(false)}
                    allowClear
                    className="menu-search-input"
                />

                {/* Desktop Dropdown */}
                {screenWidth > 991 && searchQuery.trim() !== '' && (
                    <div
                        className="search-results-dropdown"
                        onClick={stopPropagation}
                        onTouchStart={stopPropagation}
                    >
                        {filteredSearchResults.length > 0 ? (
                            filteredSearchResults.map((item, idx) => (
                                <div key={idx} className="search-result-item">
                                    <NavLink
                                        to={item.path}
                                        onClick={async (e) => {
                                            stopPropagation(e);
                                            if (item.onClick) await item.onClick();
                                            setSearchQuery('');
                                            toggleCollapsed();
                                        }}
                                        className="search-result-link"
                                    >
                                        <div className="search-result-content">
                                            {item.breadcrumb && (
                                                <span className="search-result-breadcrumb">{item.breadcrumb}</span>
                                            )}
                                            <span className="search-result-title">{item.title}</span>
                                        </div>
                                        {item.badgeCount > 0 && (
                                            <Badge
                                                count={item.badgeCount}
                                                overflowCount={99}
                                                style={{ backgroundColor: 'red' }}
                                            />
                                        )}
                                    </NavLink>
                                </div>
                            ))
                        ) : (
                            <div className="search-no-results">
                                No matching menu items found.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile Portal Overlay Container */}
            {renderMobilePortal()}

            {/* Ant Design Controlled Menu */}
            <Menu
                mode={!topMenu || screenWidth <= 991 ? 'inline' : 'horizontal'}
                theme={darkMode && 'dark'}
                selectedKeys={[selectedKey]}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                overflowedIndicator={<FeatherIcon icon="more-vertical" />}
            >
                {Scope.checkScopes(['dashboard_index']) ? (
                    <>
                        <Menu.Item icon={!topMenu && <FeatherIcon icon="home" />} key="dashboard">
                            <NavLink onClick={toggleCollapsed} to={`${path}/dashboard`}>
                                Dashboard
                            </NavLink>
                        </Menu.Item>
                        <Menu.Item icon={!topMenu && <FeatherIcon icon="user" />} key="profile">
                            <NavLink onClick={toggleCollapsed} to={`${path}/user-management/profile/${userInfo?._id}`}>
                                Profile
                            </NavLink>
                        </Menu.Item>
                    </>
                ) : (
                    <Menu.Item icon={!topMenu && <FeatherIcon icon="home" />} key="profile">
                        <NavLink onClick={toggleCollapsed} to={`${path}/user-management/profile/${userInfo?._id}`}>
                            Profile
                        </NavLink>
                    </Menu.Item>
                )}

                {Scope.checkScopesParent(['um_roles_permissions_']) ? (
                    <SubMenu key="permissions" icon={!topMenu && <FeatherIcon icon="lock" />} title="Permission Management">
                        {Scope.checkScopes(['um_roles_permissions_index']) && (
                            <Menu.Item key="roles-permissions">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/roles-permissions`}>
                                    Permissions
                                </NavLink>
                            </Menu.Item>
                        )}

                        {Scope.checkScopes(['um_roles_permissions_history']) && (
                            <Menu.Item key="permissions">
                                <NavLink
                                    to={`${path}/user-management/permissions`}
                                    onClick={async () => {
                                        toggleCollapsed();
                                        if (unseenPermissionUpdate?.data > 0) await markPermissionUpdateSeen();
                                    }}
                                >
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            unseenPermissionUpdate?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '11px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {unseenPermissionUpdate?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "12px", fontSize: "13px" }}>Permission Change Logs</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['um_']) ? (
                    <SubMenu key="user-management" icon={!topMenu && <FeatherIcon icon="maximize" />} title="Administration">
                        {Scope.checkScopes(['um_position_index']) && (
                            <Menu.Item key="position">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/position`}>
                                    Positions
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['um_departments_index']) && (
                            <Menu.Item key="departments">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/departments`}>
                                    Departments
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['um_roles_index']) && (
                            <Menu.Item key="roles">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/roles`}>
                                    Roles
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['um_users_index']) && (
                            <Menu.Item key="users">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/users`}>
                                    Users
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['students_portal_result_access']) && (
                            <Menu.Item key="student-access">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/student-access`}>
                                    Access Control
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['result_publication_index']) && (
                            <Menu.Item key="result-publication">
                                <NavLink onClick={toggleCollapsed} to={`${path}/result-publication`}>
                                    Result Publications
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['um_logs_index']) && (
                            <Menu.Item key="logs">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/logs`}>
                                    User Logs
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['um_logs_index']) && (
                            <Menu.Item key="student-logs">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/student-logs`}>
                                    Student Logs
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['temp_controller_status']) && (
                            <Menu.Item key="temp-controller">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-management/temp-controller`}>
                                    Temp Controller Status
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopes(['other_users_for_index']) && (
                    <Menu.Item key="usersForOthers">
                        <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/usersForOthers`}>
                            Users' List
                        </NavLink>
                    </Menu.Item>
                )}

                {Scope.checkScopesParent(['ut_']) ? (
                    <SubMenu key="user-attendance" icon={!topMenu && <FeatherIcon icon="user-check" />} title="User Attendance">
                        {Scope.checkScopes(['ut_month_declarations_index']) && (
                            <Menu.Item key="monthDeclarations">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/monthDeclarations`}>
                                    Month Declarations
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ut_daySchedule_index']) && (
                            <Menu.Item key="attendanceCategories">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/attendanceCategories`}>
                                    Attendance Categories
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ut_specialDay_index']) && (
                            <Menu.Item key="special-days">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/special-days`}>
                                    Special Days
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ut_rosters_index']) && (
                            <Menu.Item key="rosterAttendances">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/rosterAttendances`}>
                                    Rostering
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ut_userAttendance_index']) && (
                            <Menu.Item key="userAttendance">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/userAttendance`}>
                                    User Attendances
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ut_user_monthly_attendances_index']) && (
                            <Menu.Item key="userMonthlyAttendances">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/userMonthlyAttendances`}>
                                    Individual Report
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ut_monthly_attendances_index']) && (
                            <Menu.Item key="monthlyAttendances">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/monthlyAttendances`}>
                                    Monthly Summaries
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ut_daySchedule_index']) && (
                            <Menu.Item key="daySchedule">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/daySchedule`}>
                                    Day Schedules
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['shift_employee']) && (
                            <Menu.Item key="shiftEmployees">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/shiftEmployees`}>
                                    Shift Employee Attendance
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['refetch_attendance_index']) && (
                            <Menu.Item key="refetch">
                                <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/refetch`}>
                                    Refetch Attendance
                                </NavLink>
                            </Menu.Item>
                        )}
                        <SubMenu style={{marginLeft: "10px"}} key="leave-management" icon={!topMenu && <FeatherIcon icon="calendar" />} title="Leave Management">
                            {Scope.checkScopes(['ut_yearly_reserved_leave_index']) && (
                                <Menu.Item style={{marginLeft: "5px"}} key="yearlyReservedLeaves">
                                    <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/yearlyReservedLeaves`}>
                                        Yearly Reserved Leaves
                                    </NavLink>
                                </Menu.Item>
                            )}
                            {Scope.checkScopes(['ut_leave_index']) && (
                                <Menu.Item style={{marginLeft: "5px"}} key="leave">
                                    <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/leave`}>
                                        General Leaves
                                    </NavLink>
                                </Menu.Item>
                            )}
                            {Scope.checkScopes(['ut_early_leave_index']) && (
                                <Menu.Item style={{marginLeft: "5px"}} key="earlyLeave">
                                    <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/earlyLeave`}>
                                        Early Leaves
                                    </NavLink>
                                </Menu.Item>
                            )}
                            {Scope.checkScopes(['ut_sandwich_index']) && (
                                <Menu.Item style={{marginLeft: "5px"}} key="sandwiches">
                                    <NavLink onClick={toggleCollapsed} to={`${path}/user-attendance/sandwiches`}>
                                        Sandwich Leaves
                                    </NavLink>
                                </Menu.Item>
                            )}
                        </SubMenu>
                    </SubMenu>
                ) : null}

                {showAdminOfficeMenu ? (
                    <SubMenu key="admin-management" icon={!topMenu && <FeatherIcon icon="file-plus" />} title="Admin Office">
                        {Scope.checkScopes(['admin_career_index']) && (
                            <Menu.Item key="careerPost">
                                <NavLink onClick={toggleCollapsed} to={`${path}/admin/careerPost`}>
                                    Vacancy Post
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['admin_career_index']) && (
                            <Menu.Item key="career">
                                <NavLink
                                    to={`${path}/admin/career`}
                                    onClick={async () => {
                                        toggleCollapsed();
                                        if (unseenCVs?.data > 0) await markCVsSeen();
                                    }}
                                >
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            unseenCVs?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {unseenCVs?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "12px" }}>Direct Applications</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['admin_talent_index']) && (
                            <Menu.Item key="talentPools">
                                <NavLink
                                    to={`${path}/admin/talentPools`}
                                    onClick={async () => {
                                        toggleCollapsed();
                                        if (unseenTalentCVs?.data > 0) await markTalentCVsSeen();
                                    }}
                                >
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            unseenTalentCVs?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {unseenTalentCVs?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "12px" }}>Prospective Talent</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['admin_message_index']) && (
                            <Menu.Item key="message">
                                <NavLink
                                    to={`${path}/admin/message`}
                                    onClick={async () => {
                                        toggleCollapsed();
                                        if (unseenMessages?.data > 0) await markMessagesSeen();
                                    }}
                                >
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            unseenMessages?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {unseenMessages?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "10px" }}>Website Messages</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['admin_comment_index']) && comment?.data?.length ? (
                            <Menu.Item key="comment">
                                <NavLink
                                    to={`${path}/admin/comment`}
                                    onClick={async () => {
                                        toggleCollapsed();
                                        if (unseenComments?.data > 0) await markCommentsSeen();
                                    }}
                                >
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            unseenComments?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {unseenComments?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "10px" }}>Parents Feedback</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        ) : null}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['kpi_']) ? (
                    <SubMenu key="kpi" icon={!topMenu && <FeatherIcon icon="bar-chart-2" />} title="KPI System">
                        {Scope.checkScopes(['kpi_criteria_index']) && (
                            <Menu.Item key="criteria">
                                <NavLink onClick={toggleCollapsed} to={`${path}/kpi/criteria`}>
                                    KPI Criteria
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['kpi_index']) && (
                            <Menu.Item key="kpi-users">
                                <NavLink onClick={toggleCollapsed} to={`${path}/kpi/kpi-users`}>
                                    KPI Users
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null }

                {Scope.checkScopesParent(['schedules_']) ? (
                    <SubMenu key="schedulesManagement" icon={!topMenu && <FeatherIcon icon="loader" />} title="Schedule Management">
                        {Scope.checkScopes(['schedules_index']) && (
                            <Menu.Item key="schedules">
                                <NavLink onClick={toggleCollapsed} to={`${path}/schedules`}>
                                    Schedules
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null }

                {Scope.checkScopesParent(['website_']) ? (
                    <SubMenu key="blog-management" icon={!topMenu && <FeatherIcon icon="file-text" />} title="Website Management">
                        {Scope.checkScopes(['blogs_index']) && (
                            <Menu.Item key="blogs">
                                <NavLink onClick={toggleCollapsed} to={`${path}/blogs/blogs`}>
                                    News & Events
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['slides_index']) && (
                            <Menu.Item key="slides">
                                <NavLink onClick={toggleCollapsed} to={`${path}/slides/slides`}>
                                    Slides
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['galleries_index']) && (
                            <Menu.Item key="galleries">
                                <NavLink onClick={toggleCollapsed} to={`${path}/galleries/galleries`}>
                                    Gallery Images
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['admission_slide_index']) && (
                            <Menu.Item key="admissionSlides">
                                <NavLink onClick={toggleCollapsed} to={`${path}/admissionSlides/admissionSlides`}>
                                    Admission Slide Images
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['result_slide_index']) && (
                            <Menu.Item key="resultSlides">
                                <NavLink onClick={toggleCollapsed} to={`${path}/resultSlides/resultSlides`}>
                                    Result Slide Images
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['result_index']) && (
                            <Menu.Item key="results">
                                <NavLink onClick={toggleCollapsed} to={`${path}/results/results`}>
                                    O & A Level Result
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null }

                {Scope.checkScopesParent(['mailsBox_']) ? (
                    <SubMenu key="email-management" icon={!topMenu && <FeatherIcon icon="file-text" />} title="Email Forwarding">
                        {Scope.checkScopes(['mailsBox__index']) && (
                            <Menu.Item key="emails">
                                <NavLink onClick={toggleCollapsed} to={`${path}/emails/emails`}>
                                    Compose Emails
                                </NavLink>
                            </Menu.Item>
                        )}

                        {Scope.checkScopes(['be_forward_index']) && (
                            <Menu.Item key="forward-to">
                                <NavLink onClick={toggleCollapsed} to={`${path}/emails/forward-to`}>
                                    Add/Forward
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null }

                {Scope.checkScopesParent(['admitted_']) ? (
                    <SubMenu key="student-management" icon={!topMenu && <FeatherIcon icon="users" />} title="Manage Students">
                        {Scope.checkScopes(['admitted_students_index']) && (
                            <Menu.Item key="students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/students/students`}>
                                    All Students
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_Students_index']) && (
                            <Menu.Item key="all-students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/students/all-students`}>
                                    Academic Students
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['student_marks_entry_students_index']) && (
                            <Menu.Item key="marks-students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/students/marks-students`}>
                                    Marks Entry Form
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent([
                    'academic_setup_periods_index',
                    'academic_setup_sessions_index',
                    'academic_setup_classes_index',
                    'academic_setup_sections_index',
                    'academic_setup_subjects_index',
                    'academic_setup_payment_types_index',
                    'routines_index',
                    'routines_substitute_index',
                    'routine_pre_routine_index',
                    'routines_pre_routine_index',
                    'academic_coordination_index',
                    'academic_notices_index',
                    'academic_notes_index',
                    'academic_attendance_index',
                    'subject_chosen_students_index',
                    'academic_subjective_attendance_index',
                    'academic_predicted_grade_index',
                    'academic_predicted_grade_report',
                    'academic_payments_index',
                    'academic_transactions_index',
                    'academic_my_students_index',
                    'academic_my_students_routine',
                    'student_id_card_index',
                    'academic_result_quarter_index',
                    'academic_result_half_yearly_index',
                    'academic_result_final_index',
                    'academic_result_mock_index',
                    'academic_reports_quarter_index',
                    'academic_reports_half_yearly_index',
                    'academic_reports_final_index',
                    'academic_reports_mock_index',
                    'academic_reports_students_performance_index'
                ]) ? (
                    <SubMenu key="academic-management" icon={!topMenu && <FeatherIcon icon="book-open" />} title="Academic">
                        {Scope.checkScopesParent([
                            'academic_setup_periods_index',
                            'academic_setup_sessions_index',
                            'academic_setup_classes_index',
                            'academic_setup_sections_index',
                            'academic_setup_subjects_index',
                            'academic_setup_payment_types_index'
                        ]) ? (
                            <SubMenu key="setup-management" title="Setup" style={{paddingLeft: '35px'}}>
                                {Scope.checkScopes(['academic_setup_periods_index']) && (
                                    <Menu.Item key="periods">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/setup/periods`}>
                                            Periods
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_setup_sessions_index']) && (
                                    <Menu.Item key="sessions">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/setup/sessions`}>
                                            Sessions
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_setup_classes_index']) && (
                                    <Menu.Item key="classes">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/setup/classes`}>
                                            Classes
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_setup_sections_index']) && (
                                    <Menu.Item key="sections">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/setup/sections`}>
                                            Sections
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_setup_subjects_index']) && (
                                    <Menu.Item key="subjects">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/setup/subjects`}>
                                            Subjects
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_setup_payment_types_index']) && (
                                    <Menu.Item key="payment-types">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/setup/payment-types`}>
                                            Payment Types
                                        </NavLink>
                                    </Menu.Item>
                                )}
                            </SubMenu>
                        ) : null}

                        {Scope.checkScopesParent([
                            'routines_index',
                            'routines_substitute_index',
                            'routine_pre_routine_index',
                            'routines_pre_routine_index'
                        ]) ? (
                            <SubMenu key="routine-management" title={<span style={{marginLeft: "35px"}}>Manage Routine</span>}>
                                {Scope.checkScopes(['routines_index']) && (
                                    <Menu.Item key="routines">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/routines`}>
                                            <span style={{marginLeft: "35px"}}>Routines</span>
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['routines_substitute_index']) && (
                                    <Menu.Item key="substitute-routines">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/substitute-routines`}>
                                            <span style={{marginLeft: "35px"}}>Substitutes</span>
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['routine_pre_routine_index', 'routines_pre_routine_index']) && (
                                    <Menu.Item key="pre-routine">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/pre-routine`}>
                                            <span style={{marginLeft: "35px"}}>Pre Routines</span>
                                        </NavLink>
                                    </Menu.Item>
                                )}
                            </SubMenu>
                        ) : null }

                        {Scope.checkScopes(['academic_coordination_index']) && (
                            <Menu.Item key="coordination">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/coordination`}>
                                    Coordination
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_notices_index']) && (
                            <Menu.Item key="notice">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/notice`}>
                                    Class Teacher Zone
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_notes_index']) && (
                            <Menu.Item key="note">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/note`}>
                                    Subject Notes & Classwork
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_attendance_index']) && (
                            <Menu.Item key="attendance">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/attendance`}>
                                    Class Attendance
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['subject_chosen_students_index']) && (
                            <Menu.Item key="subject-chosen-students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/students/subject-chosen-students`}>
                                    Subjective Students
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_subjective_attendance_index']) && (
                            <Menu.Item key="subjective-attendance">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/subjective-attendance`}>
                                    Subjective Attendance
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_predicted_grade_index']) && (
                            <Menu.Item key="predicted-grade">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/predicted-grade`}>
                                    Predicted Grades
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_predicted_grade_report']) && (
                            <Menu.Item key="predicted-grade-report">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/predicted-grade-report`}>
                                    Predicted Grade Reports
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_transcript_report']) && (
                            <Menu.Item key="academic-transcript-report">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/academic-transcript-report`}>
                                    Academic Transcript
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_payments_index']) && (
                            <Menu.Item key="payments">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/payments`}>
                                    Payments
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_transactions_index']) && (
                            <Menu.Item key="transactions">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/transactions`}>
                                    Transactions
                                </NavLink>
                            </Menu.Item>
                        )}

                        {Scope.checkScopes(['academic_my_students_index']) && (
                            <Menu.Item key="my-students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/students/my-students`}>
                                    My Students
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['academic_my_students_routine']) && (
                            <Menu.Item key="my-students-routine">
                                <NavLink onClick={toggleCollapsed} to={`${path}/students/my-students-routine`}>
                                    My Class Routine
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['student_id_card_index']) && (
                            <Menu.Item key="id-card">
                                <NavLink onClick={toggleCollapsed} to={`${path}/students/id-card`}>
                                    ID Cards
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopesParent([
                            'academic_result_quarter_index',
                            'academic_result_half_yearly_index',
                            'academic_result_final_index',
                            'academic_result_mock_index'
                        ]) ? (
                            <SubMenu key="result-management" title="Marks" style={{paddingLeft: '35px'}}>
                                {Scope.checkScopes(['academic_result_quarter_index']) && (
                                    <Menu.Item key="quarter">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/result/quarter`}>
                                            Quarter
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_result_half_yearly_index']) && (
                                    <Menu.Item key="half-yearly">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/result/half-yearly`}>
                                            Half Yearly
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_result_final_index']) && (
                                    <Menu.Item key="final">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/result/final`}>
                                            Final
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_result_mock_index']) && (
                                    <Menu.Item key="mock">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/result/mock`}>
                                            Mock
                                        </NavLink>
                                    </Menu.Item>
                                )}
                            </SubMenu>
                        ) : null}
                        {Scope.checkScopesParent([
                            'academic_reports_quarter_index',
                            'academic_reports_half_yearly_index',
                            'academic_reports_final_index',
                            'academic_reports_mock_index',
                            'academic_reports_students_performance_index'
                        ]) ? (
                            <SubMenu key="reports-management" title="Reports" style={{paddingLeft: '35px'}}>
                                {Scope.checkScopes(['academic_reports_quarter_index']) && (
                                    <Menu.Item key="quarter-report">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/reports/quarter`}>
                                            Quarter
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_reports_half_yearly_index']) && (
                                    <Menu.Item key="half-yearly-report">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/reports/half-yearly`}>
                                            Half Yearly
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_reports_final_index']) && (
                                    <Menu.Item key="final-report">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/reports/final`}>
                                            Final
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_reports_mock_index']) && (
                                    <Menu.Item key="mock-report">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/reports/mock`}>
                                            Mock
                                        </NavLink>
                                    </Menu.Item>
                                )}
                                {Scope.checkScopes(['academic_reports_students_performance_index']) && (
                                    <Menu.Item key="performance">
                                        <NavLink onClick={toggleCollapsed} to={`${path}/academic/reports/performance`}>
                                            Performance
                                        </NavLink>
                                    </Menu.Item>
                                )}
                            </SubMenu>
                        ) : null}
                    </SubMenu>
                ) : null}

                {Scope.checkScopes(['academic_section_students_index']) && (
                    <Menu.Item icon={!topMenu && <FeatherIcon icon="users" />} key="section-students">
                        <NavLink onClick={toggleCollapsed} to={`${path}/students/section-students`}>
                            Students
                        </NavLink>
                    </Menu.Item>
                )}
                {Scope.checkScopes(['academic_subject_students_index']) && (
                    <Menu.Item icon={!topMenu && <FeatherIcon icon="book" />} key="subject-students">
                        <NavLink onClick={toggleCollapsed} to={`${path}/students/subject-students`}>
                            Subjective Mock Reports
                        </NavLink>
                    </Menu.Item>
                )}

                {Scope.checkScopes(['new_students_index']) && (
                    <Menu.Item icon={!topMenu && <FeatherIcon icon="users" />} key="new-students">
                        <NavLink onClick={toggleCollapsed} to={`${path}/students/new-students`}>
                            <Badge
                                style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                count={
                                    unassignedStudentCount > 0 ? (
                                        <div
                                            style={{
                                                backgroundColor: 'red',
                                                color: 'white',
                                                padding: '6px',
                                                borderRadius: '50px',
                                                fontSize: '12px',
                                                minWidth: '20px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {unassignedStudentCount}
                                        </div>
                                    ) : 0
                                }
                                size="default"
                                offset={[10, 0]}
                            >
                                <span style={{ marginRight: "10px" }}>New Students</span>
                            </Badge>
                        </NavLink>
                    </Menu.Item>
                )}

                {Scope.checkScopesParent(['student_fingerprint_']) || Scope.checkScopesParent(['student_holiday_']) ? (
                    <SubMenu key="Students-Attendance" icon={!topMenu && <FeatherIcon icon="check-circle" />} title="Students' Attendance">
                        {Scope.checkScopes(['student_fingerprint_sms_index']) && (
                            <Menu.Item icon={!topMenu && <FeatherIcon icon="clock" /> } key="attendance-message">
                                <NavLink onClick={toggleCollapsed} to={`${path}/message`}>
                                    Check In & Out
                                </NavLink>
                            </Menu.Item>
                        )}

                        {Scope.checkScopes(['student_holiday_sms_index']) && (
                            <Menu.Item icon={!topMenu && <FeatherIcon icon="lock" />} key="holiDaySMS">
                                <NavLink onClick={toggleCollapsed} to={`${path}/holiDaySMS`}>
                                    HoliDay SMS Status
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['class_marks_']) ? (
                    <SubMenu key="permission-management" icon={!topMenu && <FeatherIcon icon="lock" />} title="Marks Input Permission">
                        {Scope.checkScopes(['class_marks_input_permission_view']) && (
                            <Menu.Item key="marksInputPermission">
                                <NavLink onClick={toggleCollapsed} to={`${path}/marksInputPermission`}>
                                    Permission View
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['class_marks_input_permission_view']) && (
                            <Menu.Item key="allowUserMarksInput">
                                <NavLink onClick={toggleCollapsed} to={`${path}/allowUserMarksInput`}>
                                    Allow Users Marks Input
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['attendance_permission_']) ? (
                    <SubMenu key="attendance-permission-management" icon={!topMenu && <FeatherIcon icon="lock" />} title="Attend Input Permission">
                        {Scope.checkScopes(['attendance_input_permission_view']) && (
                            <Menu.Item key="attendanceInputPermission">
                                <NavLink onClick={toggleCollapsed} to={`${path}/attendanceInputPermission`}>
                                    Attend Permission View
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['attendance_input_permission_view']) && (
                            <Menu.Item key="allowUserAttendanceInput">
                                <NavLink onClick={toggleCollapsed} to={`${path}/allowUserAttendanceInput`}>
                                    Allow Users Attend Input
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['it_']) ? (
                    <SubMenu key="it-component" icon={!topMenu && <FeatherIcon icon="server" />} title="IT Components">
                        {Scope.checkScopes(['it_class_rooms_index']) && (
                            <Menu.Item key="classrooms">
                                <NavLink onClick={toggleCollapsed} to={`${path}/academic/setup/classrooms`}>
                                    Locations
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['it_product_category_index']) && (
                            <Menu.Item key="productCategories">
                                <NavLink onClick={toggleCollapsed} to={`${path}/IT/productCategories`}>
                                    Product Categories
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['it_component_index']) && (
                            <Menu.Item key="itComponents">
                                <NavLink onClick={toggleCollapsed} to={`${path}/IT/itComponents`}>
                                    Component List
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['it_component_index']) && (
                            <Menu.Item key="ipAddresses">
                                <NavLink onClick={toggleCollapsed} to={`${path}/IT/ipAddresses`}>
                                    IP Addresses
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['salary_']) ? (
                    <SubMenu key="user-salary" icon={!topMenu && <FeatherIcon icon="user-check" />} title="Employee Salary">
                        {Scope.checkScopes(['salary_declaration_index']) && (
                            <Menu.Item key="salaryDeclaration">
                                <NavLink onClick={toggleCollapsed} to={`${path}/employee-salary/salaryDeclaration`}>
                                    Salary Declarations
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['salary_month_declaration_index']) && (
                            <Menu.Item key="SalaryMonthDeclaration">
                                <NavLink onClick={toggleCollapsed} to={`${path}/employee-salary/SalaryMonthDeclaration`}>
                                    Salary Month Declarations
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['salary_increment_month_declaration_index']) && (
                            <Menu.Item key="SalaryIncrementMonthDeclaration">
                                <NavLink onClick={toggleCollapsed} to={`${path}/employee-salary/SalaryIncrementMonthDeclaration`}>
                                    Salary Inc. Month Declarations
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['monthly_salary_index']) && (
                            <Menu.Item key="MonthlySalary">
                                <NavLink onClick={toggleCollapsed} to={`${path}/employee-salary/MonthlySalary`}>
                                    Monthly Salary Generation
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['provident_fund_employees_index']) && (
                            <Menu.Item key="providentFundEmployees">
                                <NavLink onClick={toggleCollapsed} to={`${path}/employee-salary/providentFundEmployees`}>
                                    PF Employees
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['domain_']) ? (
                    <SubMenu key="domain-email" icon={!topMenu && <FeatherIcon icon="inbox" />} title="Emails">
                        {Scope.checkScopes(['domain_emails_teachers_index']) && (
                            <Menu.Item key="teachers">
                                <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/teachers`}>
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            teacherUnseenEmails?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {teacherUnseenEmails?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "10px" }}>My Official Emails</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['domain_emails_students_index']) && (
                            <Menu.Item key="students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/students`}>
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            studentUnseenEmails?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {studentUnseenEmails?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "10px" }}>Students' Emails</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}

                        {Scope.checkScopes(['domain_emails_class_teacher_zoom_index']) && (
                            <Menu.Item key="class-teacher-zoom-email">
                                <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/class-teacher-zoom-email`}>
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            classTeacherZoomUnseenEmails?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {classTeacherZoomUnseenEmails?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "10px" }}>Class-Teacher Zoom</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}

                        {Scope.checkScopes(['domain_emails_all_class_teacher_zoom_index']) && (
                            <Menu.Item key="all-class-teacher-zoom-email">
                                <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/all-class-teacher-zoom-email`}>
                                    <Badge
                                        style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                        count={
                                            classTeacherZoomUnseenEmails?.data > 0 ? (
                                                <div
                                                    style={{
                                                        backgroundColor: 'red',
                                                        color: 'white',
                                                        padding: '6px',
                                                        borderRadius: '50px',
                                                        fontSize: '12px',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {classTeacherZoomUnseenEmails?.data}
                                                </div>
                                            ) : 0
                                        }
                                        size="default"
                                        offset={[10, 0]}
                                    >
                                        <span style={{ marginRight: "10px" }}>All Class-Teacher Zoom</span>
                                    </Badge>
                                </NavLink>
                            </Menu.Item>
                        )}

                        {Scope.checkScopes(['email_group_index']) && (
                            <Menu.Item icon={!topMenu && <FeatherIcon icon="users" />} key="emailGroup">
                                <NavLink onClick={toggleCollapsed} to={`${path}/emailGroup`}>
                                    Email Group
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['all_d_']) ? (
                    <SubMenu key="domain-email-all" icon={!topMenu && <FeatherIcon icon="inbox" />} title="All Zoom Info">
                        {Scope.checkScopes(['all_d_teachers_info']) && (
                            <Menu.Item key="zoom-teachers">
                                <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/zoom-teachers`}>
                                    All Teacher's Zoom Info
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['all_d_students_']) ? (
                    <SubMenu key="student-domain-email-all" icon={!topMenu && <FeatherIcon icon="inbox" />} title="All Students' Zoom">
                        {Scope.checkScopes(['all_d_students_info']) && (
                            <Menu.Item key="zoom-students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/zoom-students`}>
                                    All Students' Zoom Info
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['all_d_students_index']) && (
                            <Menu.Item key="all-zoom-students">
                                <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/all-zoom-students`}>
                                    All Students' Emails
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopes(['all_official_emails_index']) && (
                    <Menu.Item key="all-official">
                        <NavLink onClick={toggleCollapsed} to={`${path}/domain-emails/all-official`}>
                            <Badge
                                style={{ marginLeft: "10px", marginTop: '7px', padding: '10px' }}
                                count={
                                    unseenOfficialEmails?.data > 0 ? (
                                        <div
                                            style={{
                                                backgroundColor: 'red',
                                                color: 'white',
                                                padding: '6px',
                                                borderRadius: '50px',
                                                fontSize: '12px',
                                                minWidth: '20px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {unseenOfficialEmails?.data}
                                        </div>
                                    ) : 0
                                }
                                size="default"
                                offset={[10, 0]}
                            >
                                <span style={{ marginRight: "20px" }}>All Official Emails</span>
                            </Badge>
                        </NavLink>
                    </Menu.Item>
                )}

                {Scope.checkScopesParent(['appointed_']) ? (
                    <SubMenu key="appointment" icon={!topMenu && <FeatherIcon icon="inbox" />} title="Appointments">
                        {Scope.checkScopes(['appointed_teachers_index']) && (
                            <Menu.Item key="appointed-teachers">
                                <NavLink onClick={toggleCollapsed} to={`${path}/appointment/appointed-teachers`}>
                                    Appointed Teachers
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['appointments_index']) && (
                            <Menu.Item key="get-appointments">
                                <NavLink onClick={toggleCollapsed} to={`${path}/appointment/get-appointments`}>
                                    Appointments
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['mcw_schedule_']) ? (
                    <SubMenu key="mcw_schedule" icon={!topMenu && <FeatherIcon icon="book-open" />} title="MCW Schedule">
                        {Scope.checkScopes(['mcw_schedule_index']) && (
                            <Menu.Item key="mcwSchedule">
                                <NavLink onClick={toggleCollapsed} to={`${path}/mcwSchedule`}>
                                    Manage Schedules
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['exam_calendar_']) ? (
                    <SubMenu key="exam_calendar" icon={!topMenu && <FeatherIcon icon="book-open" />} title="Exam Calendar">
                        {Scope.checkScopes(['exam_calendar_index']) && (
                            <Menu.Item key="examCalendar">
                                <NavLink onClick={toggleCollapsed} to={`${path}/examCalendar`}>
                                    Exam Calendar
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['clubs_']) ? (
                    <SubMenu key="club" icon={!topMenu && <FeatherIcon icon="book-open" />} title="Clubs">
                        {Scope.checkScopes(['main_clubs_index']) && (
                            <Menu.Item key="mainClub">
                                <NavLink onClick={toggleCollapsed} to={`${path}/clubs/mainClub`}>
                                    Main Clubs
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['sub_clubs_index']) && (
                            <Menu.Item key="subClub">
                                <NavLink onClick={toggleCollapsed} to={`${path}/clubs/subClub`}>
                                    Sub Clubs
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['student_club_memberships_index']) && (
                            <Menu.Item key="studentMembershipClub">
                                <NavLink onClick={toggleCollapsed} to={`${path}/clubs/studentMembershipClub`}>
                                    Student Membership Club
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['ct_schedule_']) ? (
                    <SubMenu key="ct_schedule" icon={!topMenu && <FeatherIcon icon="book-open" />} title="CT Schedule">
                        {Scope.checkScopes(['ct_schedule_index']) && (
                            <Menu.Item key="ctSchedule">
                                <NavLink onClick={toggleCollapsed} to={`${path}/ctSchedule`}>
                                    Manage Schedules
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['ct_schedule_view_index']) && (
                            <Menu.Item key="ctScheduleView">
                                <NavLink onClick={toggleCollapsed} to={`${path}/ctScheduleView`}>
                                    View Schedules
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}

                {Scope.checkScopesParent(['invoice_']) ? (
                    <SubMenu key="billing-section" icon={!topMenu && <FeatherIcon icon="inbox" />} title="Billing Section">
                        {Scope.checkScopes(['invoice_index']) && (
                            <Menu.Item icon={!topMenu && <FeatherIcon icon="users" />} key="invoice">
                                <NavLink onClick={toggleCollapsed} to={`${path}/invoice`}>
                                    General Invoice
                                </NavLink>
                            </Menu.Item>
                        )}
                        {Scope.checkScopes(['email_server_invoice_index']) && (
                            <Menu.Item icon={!topMenu && <FeatherIcon icon="users" />} key="email-server-invoice">
                                <NavLink onClick={toggleCollapsed} to={`${path}/email-server-invoice`}>
                                    Email Server Invoice
                                </NavLink>
                            </Menu.Item>
                        )}
                    </SubMenu>
                ) : null}
            </Menu>
        </div>
    );
};

MenuItems.propTypes = {
    darkMode: propTypes.bool,
    topMenu: propTypes.bool,
    toggleCollapsed: propTypes.func,
};

export default MenuItems;